import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, Users } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const EXPENSE_COLORS = ["#ef4444", "#f97316", "#a855f7", "#6366f1", "#ec4899", "#14b8a6", "#64748b", "#84cc16"];
const CHART_TOOLTIP_STYLE = { background: "#18181b", border: "1px solid #27272a", borderRadius: 4 };
const CHART_LABEL_STYLE = { color: "#fafafa", fontWeight: 600 };
const CHART_ITEM_STYLE = { color: "#fafafa" };

export function ExpensesSection({ expByCategory, expByVendor, totalExpenses }) {
  if (!expByCategory.length && !expByVendor.length) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <Card className="lg:col-span-6 border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Receipt className="w-4 h-4" /> Expenses by Category
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="expenses-by-category">
          {expByCategory.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">No expense data — sync from QuickBooks to populate</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={expByCategory} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={75} strokeWidth={1} stroke="#09090b">
                    {expByCategory.map((_, i) => <Cell key={`cat-${i}`} fill={EXPENSE_COLORS[i % EXPENSE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} itemStyle={CHART_ITEM_STYLE} formatter={(v) => [`$${v.toLocaleString()}`, "Expense"]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {expByCategory.map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: EXPENSE_COLORS[i % EXPENSE_COLORS.length] }} /><span className="text-muted-foreground">{c.category}</span></div>
                    <span className="font-semibold text-red-400">${c.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-6 border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Users className="w-4 h-4" /> Expenses by Vendor
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="expenses-by-vendor">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <TableHead className="text-xs font-semibold uppercase tracking-wider">Vendor</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Amount</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">% of Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expByVendor.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No vendor data</TableCell></TableRow>}
              {expByVendor.slice(0, 10).map((v, i) => (
                <TableRow key={v.vendor} data-testid={`vendor-expense-row-${i}`}>
                  <TableCell className="font-medium text-sm">{v.vendor}</TableCell>
                  <TableCell className="text-right font-semibold text-sm text-red-400">${v.amount.toLocaleString()}</TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">{totalExpenses ? Math.round((v.amount / totalExpenses) * 100) : 0}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
